// Order documents spell out a contractor's full legal name
// ("LAUVJAH SPEDYCJA SP. Z O.O.", "Synthos S.A."), but Marcos stores a shorter
// name ("LAUVJAH SPEDYCJA", "SYNTHOS") and searches with OData `contains` — a
// substring match. The full legal name is NOT a substring of the stored name,
// so a naive search returns nothing. We strip legal-form suffixes and
// punctuation to recover the searchable core of the name.

// Common legal forms (PL/DE/NL/EU). Matched case-insensitively as whole tokens
// or trailing fragments; dots/spaces inside are tolerated.
const LEGAL_FORM_PATTERNS = [
  /\bsp[.\s]*z[.\s]*o[.\s]*o[.\s]*/gi, // sp. z o.o.
  /\bspółka z ograniczoną odpowiedzialnością\b/gi,
  /\bs[.\s]*a\b\.?/gi, // S.A.
  /\bsp[.\s]*j\b\.?/gi, // sp. j.
  /\bsp[.\s]*k\b\.?/gi, // sp. k.
  /\bs[.\s]*c\b\.?/gi, // s.c.
  /\bgmbh(\s*&\s*co\.?\s*kg)?\b/gi,
  /\bb[.\s]*v\b\.?/gi, // B.V.
  /\bn[.\s]*v\b\.?/gi, // N.V.
  /\ba[.\s]*g\b\.?/gi, // AG
  /\bs[.\s]*r[.\s]*l\b\.?/gi, // S.r.l.
  /\bltd\b\.?/gi,
  /\bllc\b\.?/gi,
  /\bkg\b\.?/gi,
  /\boo\b/gi,
]

/**
 * Reduce an order's full contractor name to a Marcos-searchable query: drop
 * legal-form suffixes and collapse punctuation/whitespace. Returns undefined
 * when nothing usable remains.
 */
export function normalizeContractorQuery(
  name: string | undefined,
): string | undefined {
  if (!name) return undefined
  let s = name
  for (const re of LEGAL_FORM_PATTERNS) s = s.replace(re, " ")
  // Collapse remaining punctuation and whitespace.
  s = s.replace(/[.,;]/g, " ").replace(/\s+/g, " ").trim()
  return s.length > 0 ? s : undefined
}

// Order NIPs may carry a "PL" VAT prefix, dashes or spaces
// ("PL 783-182-27-02"), while Marcos' TIN column is digits-only. Strip
// everything but digits so the two compare equal.
export function normalizeNip(nip: string | undefined): string | undefined {
  const digits = (nip || "").replace(/\D/g, "")
  return digits.length > 0 ? digits : undefined
}

type NamedContractor = { ShortName?: string; Name?: string }

const canon = (s: string) =>
  (normalizeContractorQuery(s) || s).toLowerCase().replace(/\s+/g, " ").trim()

/**
 * Pick the best contractor from search results for the given order name.
 * Prefers an exact match on the normalized name, then a result whose name
 * starts with the query, otherwise the first result. Returns undefined when
 * the list is empty.
 */
export function pickBestContractor<T extends NamedContractor>(
  results: T[],
  orderName: string | undefined,
): T | undefined {
  if (results.length === 0) return undefined
  if (!orderName) return results[0]

  const target = canon(orderName)
  const nameOf = (c: T) => canon(c.ShortName || c.Name || "")

  return (
    results.find((c) => nameOf(c) === target) ??
    results.find(
      (c) => nameOf(c).startsWith(target) || target.startsWith(nameOf(c)),
    ) ??
    results[0]
  )
}
